import {
    EventrDate,
    generateID,
    getUserOrgPref,
    IEventInformationData,
    IItinerarymap,
    IRCChannel,
    spError,
    structuredCloneL,
    switchUnixToMs,
    V2,
} from '@eventr/common'
import { createSelector } from 'reselect'
import { StateCreator } from 'zustand'
import { shallow } from 'zustand/shallow'
import { DataAPI, IGeneralInfo } from '../../data/Data'
import { useDeviceInfo } from '../../feat/appState/deviceInfo'
import { isCalendarEnabled } from '../../feat/calendar/calendarProvider'
import { useGuestStore } from '../../feat/guest/guest.store'
import accomodationRenderOptions from '../../v2/renderer_options_example/accommodation.json'
import activitiesRenderOptions from '../../v2/renderer_options_example/activities.json'
import eventInfoRenderOptions from '../../v2/renderer_options_example/eventInformationV2.json'
import flightsRenderOptions from '../../v2/renderer_options_example/flights.json'
import hireCarsRenderOptions from '../../v2/renderer_options_example/hireCars.json'
import parkingRenderOptions from '../../v2/renderer_options_example/parkingBookings.json'
import transportRenderOptions from '../../v2/renderer_options_example/transport.json'
import vehicleAllocationsRenderOptions from '../../v2/renderer_options_example/vehicle_allocation.json'
import { BoundStore, bs, useBoundStore } from '../bound.store'
import { IItinerary } from '../interfaces'
import { loadingSignal } from '../overlayState/overlaySignals'
import { removeOldItineraryData } from './helpers'
interface ItineraryMapState {
    itineraryMap: IItinerarymap[]
    noItineraries: boolean
    itineraryData: { [key: string]: IItinerary }
}

interface ItineraryMapActions {
    setItineraryMap: (itineraryMap: IItinerarymap[]) => void
    setToNextItinerary: () => void
    getSelectedItinCode: () => string
    findCurrentItineraryIndex: () => number
    forceItineraryMapUpdate: () => Promise<any[]>
    createItinerary: (itinerary: IEventInformationData) => Promise<any>
    resolveItinId: () => Promise<void>
    deleteItinerary: (itineraryID: number) => Promise<void>
    getItineraryByID: (id: number) => IItinerary | undefined
    pullEventData: (itineraryId: number) => Promise<IItinerary | null>
}

export interface ItineraryMapSlice
    extends ItineraryMapState,
        ItineraryMapActions {}

export const createItineraryMapSlice: StateCreator<
    BoundStore,
    [['zustand/devtools', never]],
    [],
    ItineraryMapSlice
> = (set, get) => ({
    itineraryMap: [],
    noItineraries: true,
    itineraryData: {},
    setItineraryMap: (itineraryMap) => {
        set(
            { itineraryMap, noItineraries: itineraryMap.length === 0 },
            false,
            'setItineraryMap'
        )
    },
    setToNextItinerary: () => {
        console.log('setToNextItinerary')

        const { itineraryMap, setCurrentItin, findCurrentItineraryIndex } =
            get()
        if (!itineraryMap || itineraryMap.length < 1) {
            setCurrentItin(-1)
            return
        }

        const currentIndex = findCurrentItineraryIndex()

        if (currentIndex !== -1) {
            const nextIndex = currentIndex + 1
            setCurrentItin(
                nextIndex < itineraryMap.length
                    ? itineraryMap[nextIndex].id
                    : itineraryMap[0].id
            )
        } else {
            setCurrentItin(itineraryMap[0].id)
        }
    },
    getSelectedItinCode: () => {
        const { itineraryMap, selectedItinID } = get()
        if (selectedItinID === -1) {
            return ''
        }

        return (
            itineraryMap
                .map((itin) =>
                    itin.id === selectedItinID
                        ? itin.properties?.code
                        : undefined
                )
                .find((code) => code !== undefined) || ''
        )
    },
    findCurrentItineraryIndex: () => {
        const { itineraryMap, selectedItinID } = get()
        return itineraryMap.findIndex(
            (itinerary: IItinerarymap) => itinerary.id === selectedItinID
        )
    },
    forceItineraryMapUpdate: async () => {
        const { wpUser, setItineraryMap } = get()
        if (!wpUser && !useGuestStore.getState().isGuestMode) {
            return Promise.resolve([])
        }

        const newItineraryMap: IItinerarymap[] = await DataAPI.getItineraryMap()

        if (newItineraryMap) {
            const sortedNewItineraryMap = sortItineraryMap(newItineraryMap)

            setItineraryMap(sortedNewItineraryMap ?? [])

            return sortedNewItineraryMap ?? []
        }
        return Promise.resolve([])
    },
    createItinerary: async (itinerary: IEventInformationData) => {
        const { wpUser, forceItineraryMapUpdate, setCurrentItin } = get()
        loadingSignal.value = true

        const respCreate = await DataAPI.createItinerary({
            name: itinerary.title,
        })

        if (!respCreate || !respCreate.data || !respCreate.data.itineraryID) {
            /// Error
            loadingSignal.value = false
            return false
        }

        var itineraryBaseWithMembers = itinerary

        itineraryBaseWithMembers.members = [
            {
                option: wpUser?.id,
                type: 'User',
                _id: generateID(respCreate.data.itineraryID, 1),
            },
        ]

        const generalInfo: IGeneralInfo = {
            itinerary_id: respCreate.data.itineraryID,
            section_id: 1,
            value: itineraryBaseWithMembers,
        }

        const respUpdate = await DataAPI.createUpdateGeneralInfo(generalInfo)

        if (respCreate && respUpdate) {
            await forceItineraryMapUpdate()
            await setCurrentItin(respCreate.data.itineraryID)
            loadingSignal.value = false

            return true
        } else {
            loadingSignal.value = false
            return false
        }
    },
    resolveItinId: async () => {
        const { selectedOrg, itineraryMap, setCurrentItin } = get()
        if (selectedOrg) {
            if (itineraryMap && itineraryMap.length > 0) {
                const defaultItinerary = getUserOrgPref(
                    'itinerary',
                    'defaultItinerary',
                    selectedOrg.name
                )
                let currentEventID = -1
                const itineraryIds = itineraryMap.map((i) => i.id)

                if (itineraryIds.includes(defaultItinerary)) {
                    currentEventID = defaultItinerary
                } else {
                    const currentItinerary = getCurrentItinerary(itineraryMap)
                    if (currentItinerary) {
                        currentEventID = currentItinerary.id
                    }
                }
                await setCurrentItin(currentEventID)
            }
        }
    },
    deleteItinerary: async (itineraryID: number) => {
        const { forceItineraryMapUpdate, setCurrentItin, setToNextItinerary } =
            get()
        await DataAPI.deleteItinerary(itineraryID)
        const newMap = await forceItineraryMapUpdate()
        setCurrentItin(-1)

        if (newMap.length !== 0) {
            setToNextItinerary()
        }
    },
    getItineraryByID: (id: number) => {
        const { itineraryData } = get()
        const data = itineraryData[id]
        if (data) {
            return replaceRenderOptions(data)
        }
        return itineraryData[id]
    },
    pullEventData: async (itineraryId: number) => {
        const map = bs().itineraryMap
        const version = useDeviceInfo.getState().version
        const selectedOrg = bs().selectedOrg

        const itineraryMapItinerary: IItinerarymap = map.find(
            (e) => e?.id === itineraryId
        )
        if (itineraryMapItinerary) {
            const eventData: IItinerary = await DataAPI.getData(
                itineraryMapItinerary
            ).catch((e) => {
                spError(e)
                return null
            })
            if (eventData) {
                if (selectedOrg && isCalendarEnabled(version.buildVersion)) {
                    bs().syncCalendar(itineraryId, null, true)
                }

                set(
                    {
                        itineraryData: {
                            ...bs().itineraryData,
                            [itineraryId]: eventData,
                        },
                    },
                    false,
                    'itineraryData updated'
                )
            }
            return eventData
        }
    },
})

setTimeout(() => {
    useBoundStore.subscribe(
        (state) => state.itineraryMap,
        (map) => {
            if (bs().loginCompleted) {
                const selectedItinID = bs().selectedItinID

                if (selectedItinID !== -1) {
                    const localItineraryData =
                        bs().getItineraryByID(selectedItinID)

                    const mapVersion = Number.parseInt(
                        map.find((i) => i?.id === selectedItinID)
                            ?.itinerary_version ?? '0'
                    )
                    if (!mapVersion) {
                        bs().setCurrentItin(-1)
                    }

                    if (localItineraryData?.version !== mapVersion) {
                        bs().pullEventData(
                            getItineraryMapByID(selectedItinID)?.id ?? -1
                        )
                    }

                    removeOldItineraryData(map) // TODO : replace
                }

                /// if id is -1 needs to be set if can.
                if (selectedItinID === -1 && map && map.length > 0) {
                    if (bs().selectedOrg) {
                        const defaultItinerary = getUserOrgPref(
                            'itinerary',
                            'defaultItinerary',
                            'selectedOrg.name'
                        )

                        const exist = map.some(
                            (i: IItinerarymap) => i.id === defaultItinerary
                        )

                        if (exist) {
                            bs().setCurrentItin(
                                Number.parseInt(defaultItinerary)
                            )
                        } else {
                            bs().setCurrentItin(
                                getCurrentItinerary(map)?.id ?? -1
                            )
                        }
                    }
                }
            }
        },
        { fireImmediately: true }
    )
    useBoundStore.subscribe(
        (state) => ({
            itinData: state.itineraryData,
            selectedItinID: state.selectedItinID,
        }),
        ({ itinData, selectedItinID }) => {
            if (selectedItinID !== -1) {
                const itin = itinData[selectedItinID]
                if (itin) {
                    useBoundStore.setState(
                        {
                            selectedItinerary: replaceRenderOptions(itin),
                        },
                        false,
                        'selectedItinerary updated from itineraryData'
                    )
                }
            }
        },
        { fireImmediately: true, equalityFn: shallow }
    )
}, 100)

export const getItineraryMapByID = (id: number) => {
    const map = bs().itineraryMap

    const itinMap = map.find((itineraryObj: IItinerarymap) => {
        return itineraryObj?.id === id
    })

    return itinMap
}
export const getPubItineraryName = (code: string) => {
    const map = bs().itineraryMap
    const currentItin = bs().selectedItinID

    const itinMap = map.find((itineraryObj: IItinerarymap) => {
        return itineraryObj.id === currentItin
    })
    if (itinMap) {
        return (
            itinMap?.properties?.originalItinCodes?.find((o) => o.code === code)
                ?.name ?? 'Imported Activities'
        )
    }

    return itinMap
}

function sortItineraryMap(
    unsortedItineraryMap: IItinerarymap[]
): IItinerarymap[] {
    if (!unsortedItineraryMap) {
        return []
    }
    if (unsortedItineraryMap.length < 2) {
        return [...unsortedItineraryMap]
    }

    /// Unix Now
    const timeNow = Math.floor(stripTime(new EventrDate()).getUnixMs / 1000)

    /// NEED TO ADD CURRENT
    /// Sorting based on start time. Time is probally only a date but should show future itins in order THEN past itins in order of start time... i guess.
    const oldItineraries = unsortedItineraryMap
        .filter((itinerary: IItinerarymap) => {
            return switchUnixToMs(itinerary.start_time) < timeNow
        })
        .sort((itineraryA: IItinerarymap, itineraryB: IItinerarymap) => {
            if (itineraryA.start_time > itineraryB.start_time) {
                return 1
            } else {
                return -1
            }
        })

    const futureItineries = unsortedItineraryMap
        .filter((itinerary: IItinerarymap) => {
            return switchUnixToMs(itinerary.start_time) >= timeNow
        })
        .sort((itineraryA: IItinerarymap, itineraryB: IItinerarymap) => {
            if (itineraryA.start_time > itineraryB.start_time) {
                return 1
            } else if (itineraryA.start_time === itineraryB.start_time) {
                if (itineraryA.end_time > itineraryB.end_time) {
                    return 1
                } else {
                    return -1
                }
            } else {
                return -1
            }
        })

    const toReturn = [...futureItineries, ...oldItineraries]
    return toReturn
}
const stripTime = (date: EventrDate) => {
    date.setFromObject({ hour: 12, minute: 0, second: 0, millisecond: 0 })
    return date
}

const getCurrentItinerary = (dataItin: IItinerarymap[]) => {
    try {
        let current = dataItin.find(
            (el) =>
                Date.now() < el.end_time * 1000 &&
                Date.now() > el.start_time * 1000 // *1000 to match precision
        )
        //find next upcoming itinerary
        if (current == null) {
            let smallestDiff: number
            let res
            dataItin.forEach((el) => {
                const diff = el.start_time * 1000 - Date.now()
                if (diff > 0) {
                    if (!smallestDiff || smallestDiff > diff) {
                        smallestDiff = diff
                        res = el
                    }
                }
            })
            if (res != null) {
                current = res
            }
        }
        //find the most recent itinerary
        if (current == null) {
            let smallestDiff = Date.now()
            let res
            dataItin.forEach((el) => {
                const diff = Date.now() - el.end_time * 1000
                if (diff > 0) {
                    if (smallestDiff > diff) {
                        smallestDiff = diff
                        res = el
                    }
                }
            })
            if (res != null) {
                current = res
            }
        }
        return current
    } catch {
        return undefined
    }
}

const getRenderOptions = (sectionID: number) => {
    switch (sectionID) {
        case 1:
            return eventInfoRenderOptions
        case 2:
            return flightsRenderOptions
        case 3:
            return accomodationRenderOptions
        case 4:
            return hireCarsRenderOptions
        case 5:
            return transportRenderOptions
        case 8:
            return activitiesRenderOptions
        case 9:
            return parkingRenderOptions
        case 10:
            return vehicleAllocationsRenderOptions
        default:
            return null
    }
}
export const replaceRenderOptions = (itin: IItinerary) => {
    const newV2:
        | V2[]
        | {
              rendererOptions:
                  | {
                        components: {
                            componentKey: string
                            points: (
                                | {
                                      extraMidValues: {
                                          componentKey: string
                                          darkMode: boolean
                                          typography: string
                                          value: string
                                      }[]
                                      midValue: {
                                          componentKey: string
                                          darkMode: boolean
                                          typography: string
                                          value: string
                                      }
                                      onClick: {
                                          action: string
                                          params: {
                                              key: string
                                              value: string
                                          }[]
                                      }
                                  }
                                | {
                                      midValue: {
                                          componentKey: string
                                          darkMode: boolean
                                          typography: string
                                          value: string
                                      }
                                      onClick: {
                                          action: string
                                          params: { value: string }[]
                                      }
                                      extraMidValues?: undefined
                                  }
                            )[]
                        }[]
                        settings: { displayType: string; headerIcon: string }
                    }
                  | {
                        components: { componentKey: string }[]
                        settings: {
                            dashboardKey: string
                            displayInUpNext: boolean
                            editForm: string
                            editHeader: string
                            filterCriteria: string[]
                            headerIcon: string
                            isHeaderExpandable: boolean
                            pastCriteria: string
                            pastHeader: string
                            pastHeaderIcon: string
                            primaryLabel: string
                            secondaryCard: string
                            secondaryLabel: string
                            userList: string
                            wizardHeader: string
                        }
                    }
                  | {
                        components: { componentKey: string }[]
                        settings: {
                            dashboardKey: string
                            displayInUpNext: boolean
                            editForm: string
                            editHeader: string
                            headerIcon: string
                            isHeaderExpandable: boolean
                            multiDays: boolean
                            pastCriteria: string
                            pastHeader: string
                            pastHeaderIcon: string
                            primaryLabel: string
                            secondaryCard: string
                            secondaryLabel: string
                            userList: string
                            wizardHeader: string
                        }
                    }
                  | {
                        components: (
                            | {
                                  componentKey: string
                                  height: string
                                  body?: undefined
                                  border?: undefined
                              }
                            | {
                                  body: (
                                      | {
                                            componentKey: string
                                            typography: string
                                            value: string
                                            components?: undefined
                                        }
                                      | {
                                            componentKey: string
                                            value: string
                                            components: (
                                                | {
                                                      componentKey: string
                                                      hidden: string[]
                                                      visible: string[]
                                                      points: (
                                                          | {
                                                                componentKey: string
                                                                height: string
                                                                endValue?: undefined
                                                                midValue?: undefined
                                                                noSubItemsPadding?: undefined
                                                                subItems?: undefined
                                                            }
                                                          | {
                                                                endValue: {
                                                                    addUserConfirmationHeader: string
                                                                    addUserConfirmationText: string
                                                                    channelNames: string[]
                                                                    componentKey: string
                                                                    hidden: string[]
                                                                    usersList: string
                                                                    visible: string[]
                                                                }
                                                                midValue: {
                                                                    componentKey: string
                                                                    typography: string
                                                                    value: string
                                                                }
                                                                noSubItemsPadding: boolean
                                                                subItems: (
                                                                    | {
                                                                          componentKey: string
                                                                          height: string
                                                                          value?: undefined
                                                                          typography?: undefined
                                                                          users?: undefined
                                                                      }
                                                                    | {
                                                                          componentKey: string
                                                                          value: string
                                                                          typography: string
                                                                          height?: undefined
                                                                          users?: undefined
                                                                      }
                                                                    | {
                                                                          componentKey: string
                                                                          users: string
                                                                          height?: undefined
                                                                          value?: undefined
                                                                          typography?: undefined
                                                                      }
                                                                )[]
                                                                componentKey?: undefined
                                                                height?: undefined
                                                            }
                                                      )[]
                                                  }
                                                | {
                                                      componentKey: string
                                                      hidden: string[]
                                                      visible: string[]
                                                      points: (
                                                          | {
                                                                componentKey: string
                                                                height: string
                                                                subItemsVisible?: undefined
                                                                endValue?: undefined
                                                                midValue?: undefined
                                                                noSubItemsPadding?: undefined
                                                                subItems?: undefined
                                                            }
                                                          | {
                                                                subItemsVisible: string[]
                                                                endValue: {
                                                                    addUserConfirmationHeader: string
                                                                    addUserConfirmationText: string
                                                                    channelNames: string[]
                                                                    componentKey: string
                                                                    hidden: string[]
                                                                    usersList: string
                                                                    visible: string[]
                                                                }
                                                                midValue: {
                                                                    componentKey: string
                                                                    typography: string
                                                                    value: string
                                                                }
                                                                noSubItemsPadding: boolean
                                                                subItems: (
                                                                    | {
                                                                          componentKey: string
                                                                          height: string
                                                                          value?: undefined
                                                                          typography?: undefined
                                                                          users?: undefined
                                                                      }
                                                                    | {
                                                                          componentKey: string
                                                                          value: string
                                                                          typography: string
                                                                          height?: undefined
                                                                          users?: undefined
                                                                      }
                                                                    | {
                                                                          componentKey: string
                                                                          users: string
                                                                          height?: undefined
                                                                          value?: undefined
                                                                          typography?: undefined
                                                                      }
                                                                )[]
                                                                componentKey?: undefined
                                                                height?: undefined
                                                            }
                                                      )[]
                                                  }
                                            )[]
                                            typography?: undefined
                                        }
                                  )[]
                                  border: boolean
                                  componentKey: string
                                  height?: undefined
                              }
                        )[]
                        settings: {
                            headerIcon: string
                            isHeaderExpandable: boolean
                            userList: string
                        }
                    }
              sectionID: number
              sectionName: string
              sectionDisplayName: string
              sectionDisplayNameTeams: string
              renderer: 'universal-iterable' | 'universal' | 'pastSorter'
              RCChannels: IRCChannel[]
              data: IEventInformationData | any
              sectionNameOverwrite?: string
              category?: string
              iconOverwrite?: string
              usePage?: boolean
              ogCode?: string
          }[]
        | undefined = []
    itin.v2?.forEach((v2) => {
        const rendererOptions = getRenderOptions(v2.sectionID)
        if (rendererOptions) {
            newV2.push({ ...v2, rendererOptions })
        }
    })
    const copy = structuredCloneL(itin) as IItinerary
    copy.v2 = newV2
    return copy
}

export const selectorOriginalItinCodes = createSelector(
    (state: BoundStore) => state.itineraryMap,
    (state: BoundStore) => state.selectedItinID,
    (map, itinID) => {
        const currentItin = map.find((i) => i.id === itinID)
        return currentItin?.properties?.originalItinCodes
    }
)
